// Pure domain rules for Comp of the Week (CotW).
//
// This module deliberately has no Firebase, HTTP, or UI dependencies. The
// browser uses it to render the schedule and to interpret the public event
// projection; the trusted worker uses the same functions to pick courses,
// validate submissions, and rank knockout rounds.

/* global globalThis */

import { addDays, dateKeyInZone, weekdayInZone, zonedTimeToUtc } from './kzTime.js';

export const COTW_TIME_ZONE = 'Europe/Berlin';
export const COTW_EVENT_DAY = 6; // Saturday
export const COTW_START_TIME = '17:00';
export const COTW_QUALIFIER_MINUTES = 20;
export const COTW_KNOCKOUT_MINUTES = 10;
export const COTW_INTERROUND_SECONDS = 60;
export const COTW_MAX_PRO_WR_SECONDS = 420;
export const COTW_BUFFER_SECONDS = 10;
export const COTW_FINALISTS = 4;
export const COTW_PAUSE_TOLERANCE_SECONDS = 0.25;
export const COTW_REPLAY_CONTRACT_VERSION = 'cotw-replay-v1';
export const COTW_REPLAY_REFERENCE_MAX_LENGTH = 2048;

export const COTW_PHASES = Object.freeze({
  UPCOMING: 'upcoming',
  QUALIFIER: 'qualifier',
  INTERMISSION: 'intermission',
  KNOCKOUT: 'knockout',
  COMPLETE: 'complete',
});

export const COTW_MODES = Object.freeze([
  { id: 'classic', short: 'CKZ', label: 'Classic' },
  { id: 'vanilla', short: 'VNL', label: 'Vanilla' },
]);

export const DEFAULT_COTW_CONFIG = Object.freeze({
  enabled: true,
  dayOfWeek: COTW_EVENT_DAY,
  timeZone: COTW_TIME_ZONE,
  startTime: COTW_START_TIME,
  qualifierMinutes: COTW_QUALIFIER_MINUTES,
  knockoutMinutes: COTW_KNOCKOUT_MINUTES,
  interRoundSeconds: COTW_INTERROUND_SECONDS,
  maxProWrSeconds: COTW_MAX_PRO_WR_SECONDS,
  bufferSeconds: COTW_BUFFER_SECONDS,
  pauseToleranceSeconds: COTW_PAUSE_TOLERANCE_SECONDS,
  finalists: COTW_FINALISTS,
  activeWindowHours: 8,
});

// Backwards-friendly aliases used by integrations and tests that call the
// feature "Comp" rather than CotW.
export const COTW_CONFIG_DEFAULTS = DEFAULT_COTW_CONFIG;
export const DEFAULT_CONFIG = DEFAULT_COTW_CONFIG;

const finiteNumber = (value) => {
  const number = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(number) ? number : null;
};

const firstDefined = (...values) => values.find((value) => value !== undefined && value !== null);

const firstNumber = (...values) => {
  for (const value of values) {
    const number = finiteNumber(value);
    if (number != null) return number;
  }
  return null;
};

const asObject = (value) => (value && typeof value === 'object' ? value : {});

/**
 * Replay references are opaque handles owned by the game-server/replay
 * integration. Keep the worker contract bounded and reject control
 * characters, but do not impose a URL scheme: deployments may use an object
 * key, an internal URL, or a demo identifier.
 */
export const normalizeReplayReference = (value) => {
  if (typeof value !== 'string') return null;
  const reference = value.trim();
  if (!reference || reference.length > COTW_REPLAY_REFERENCE_MAX_LENGTH) return null;
  if (
    [...reference].some((character) => character.charCodeAt(0) < 0x20 || character === '\u007f')
  ) {
    return null;
  }
  return reference;
};

const normaliseMode = (mode) => {
  const value = String(mode || '').toLowerCase();
  if (value === 'ckz' || value === 'classic') return COTW_MODES[0];
  if (value === 'vnl' || value === 'vanilla') return COTW_MODES[1];
  return null;
};

/** Return the absolute instant for a Saturday event date in Berlin. */
export const cotwStartForDate = (
  eventDate,
  timeZone = COTW_TIME_ZONE,
  startTime = COTW_START_TIME
) => zonedTimeToUtc(eventDate, startTime, timeZone);

/**
 * Find the next scheduled event strictly after `now`.
 *
 * The returned eventDate is the date in the schedule's zone, not the viewer's
 * local date. This is important around midnight and during DST transitions.
 */
export const nextCotwStart = (
  now = Date.now(),
  { timeZone = COTW_TIME_ZONE, dayOfWeek = COTW_EVENT_DAY, startTime = COTW_START_TIME } = {}
) => {
  const today = dateKeyInZone(now, timeZone);
  const todayDow = weekdayInZone(now, timeZone);
  let eventDate = addDays(today, (dayOfWeek - todayDow + 7) % 7);
  let startsAt = cotwStartForDate(eventDate, timeZone, startTime);
  if (!Number.isFinite(startsAt) || startsAt <= now) {
    eventDate = addDays(eventDate, 7);
    startsAt = cotwStartForDate(eventDate, timeZone, startTime);
  }
  return { eventDate, startsAt };
};

/**
 * Public/worker event key. Before the Saturday start this is the upcoming
 * event; during the bounded Saturday event window it is the active event.
 * Sunday/Monday return to the next upcoming key so countdowns never render a
 * stale event as if it were about to begin.
 */
export const cotwEventKeyFor = (
  now = Date.now(),
  {
    timeZone = COTW_TIME_ZONE,
    dayOfWeek = COTW_EVENT_DAY,
    startTime = COTW_START_TIME,
    activeWindowHours = 8,
  } = {}
) => {
  const upcoming = nextCotwStart(now, { timeZone, dayOfWeek, startTime });
  const previousDate = addDays(upcoming.eventDate, -7);
  const previousStart = cotwStartForDate(previousDate, timeZone, startTime);
  const activeUntil = previousStart + Math.max(0, Number(activeWindowHours) || 8) * 60 * 60 * 1000;
  return now >= previousStart && now < activeUntil ? previousDate : upcoming.eventDate;
};

/** Resolve a phase from the public event projection at an instant. */
export const phaseAt = (event, now = Date.now()) => {
  if (!event) return COTW_PHASES.UPCOMING;
  if (event.phase === COTW_PHASES.COMPLETE) return COTW_PHASES.COMPLETE;

  const qualifierStartsAt = finiteNumber(event.qualifierStartsAt ?? event.startsAt);
  const qualifierEndsAt = finiteNumber(
    event.qualifierEndsAt ??
      (qualifierStartsAt == null ? null : qualifierStartsAt + COTW_QUALIFIER_MINUTES * 60 * 1000)
  );
  if (qualifierStartsAt == null || now < qualifierStartsAt) return COTW_PHASES.UPCOMING;
  if (qualifierEndsAt != null && now < qualifierEndsAt) return COTW_PHASES.QUALIFIER;

  const rounds = Array.isArray(event.rounds) ? event.rounds : [];
  const currentRound = event.currentRound || rounds.find((round) => round.status === 'live');
  if (currentRound) {
    const startsAt = finiteNumber(currentRound.startsAt);
    const endsAt = finiteNumber(currentRound.endsAt);
    if (startsAt != null && now < startsAt) return COTW_PHASES.INTERMISSION;
    if (endsAt == null || now < endsAt) return COTW_PHASES.KNOCKOUT;
  }

  return event.phase || COTW_PHASES.INTERMISSION;
};

/**
 * Read a likely PRO world-record value from the several API shapes used by
 * CS2KZ snapshots. Unknown shapes intentionally return null, which excludes
 * the course rather than guessing that a course is eligible.
 */
export const proWorldRecordSeconds = (course, mode) => {
  const modeInfo = normaliseMode(mode);
  if (!modeInfo) return null;
  const source = asObject(course);
  const filters = asObject(source.filters);
  const modes = asObject(source.modes);
  const filter = asObject(
    firstDefined(
      filters[modeInfo.id],
      filters[modeInfo.short],
      modes[modeInfo.id],
      modes[modeInfo.short]
    )
  );
  const pro = asObject(firstDefined(filter.pro, filter.PRO, source.pro, source.PRO));
  const worldRecord = asObject(
    firstDefined(filter.worldRecord, filter.world_record, source.worldRecord, source.world_record)
  );
  return firstNumber(
    filter.proWorldRecord,
    filter.pro_world_record,
    filter.proWr,
    filter.pro_wr,
    filter.proWR,
    filter.proWrSeconds,
    filter.pro_wr_seconds,
    filter.proTime,
    filter.pro_time,
    filter.wr,
    filter.worldRecord,
    filter.world_record,
    filter.worldRecordSeconds,
    filter.world_record_seconds,
    filter.time,
    pro.worldRecord,
    pro.world_record,
    pro.wr,
    pro.time,
    worldRecord.pro,
    worldRecord.seconds,
    source[`pro_wr_${modeInfo.id}`],
    source[`proWr${modeInfo.id}`],
    source.proWorldRecords && source.proWorldRecords[modeInfo.id],
    source.pro_world_records && source.pro_world_records[modeInfo.id]
  );
};

/**
 * Return the mode's ranked/approved metadata while retaining unknown fields
 * for the worker's audit record.
 */
export const modeEligibility = (course, mode, maxProWrSeconds = COTW_MAX_PRO_WR_SECONDS) => {
  const modeInfo = normaliseMode(mode);
  if (!modeInfo) return { eligible: false, mode: mode, reason: 'unsupported-mode' };
  const source = asObject(course);
  const filters = asObject(source.filters);
  const modes = asObject(source.modes);
  const filter = asObject(
    firstDefined(
      filters[modeInfo.id],
      filters[modeInfo.short],
      modes[modeInfo.id],
      modes[modeInfo.short]
    )
  );
  const state = firstDefined(filter.state, filter.status, filter.rankState);
  const worldRecord = proWorldRecordSeconds(source, modeInfo.id);
  const stateOkay =
    state == null || ['ranked', 'approved', 'pro'].includes(String(state).toLowerCase());
  let reason = null;
  if (!stateOkay) reason = 'not-ranked';
  else if (worldRecord == null) reason = 'missing-pro-wr';
  else if (worldRecord < 0) reason = 'invalid-pro-wr';
  else if (worldRecord >= maxProWrSeconds) reason = 'pro-wr-too-slow';
  return {
    eligible: reason == null,
    mode: modeInfo.id,
    short: modeInfo.short,
    state: state ?? null,
    proWorldRecord: worldRecord,
    reason,
  };
};

const mapKey = (map) => String(map?.id ?? map?.map_id ?? map?.mapId ?? map?.name ?? '');

const mapName = (map) => map?.name || map?.mapName || mapKey(map);

/**
 * Group eligible courses by map. Selection must first choose uniformly from
 * maps, and only then uniformly from that map's eligible course/mode pairs.
 */
const recordMapKey = (record) =>
  String(record?.mapId ?? record?.map_id ?? record?.map?.id ?? record?.map ?? '');

const recordMapName = (record) =>
  String(record?.mapName ?? record?.map_name ?? record?.map?.name ?? '');

const recordCourseName = (record) =>
  String(record?.courseName ?? record?.course_name ?? record?.course?.name ?? record?.course ?? '');

const recordMode = (record) => normaliseMode(record?.mode ?? record?.filter?.mode)?.id;

const recordTime = (record) => firstNumber(record?.time, record?.runtime, record?.runtimeSeconds);

const recordIsNoTeleportNoStyle = (record) => {
  const teleports = record?.teleports ?? record?.teleportCount ?? record?.teleport_count;
  const hasTeleports = record?.hasTeleports ?? record?.has_teleports;
  const styles = record?.styles ?? record?.style ?? [];
  const styleList = Array.isArray(styles) ? styles : styles ? [styles] : [];
  return (
    (teleports == null || Number(teleports) === 0) &&
    (hasTeleports == null || hasTeleports === false) &&
    styleList.length === 0
  );
};

/**
 * Join authoritative top-record rows to the approved map/course list. The
 * `/maps` endpoint has no WR time; when `records` is supplied, metadata alone
 * can never make a course eligible.
 */
export const eligibleCotwMaps = (
  maps,
  { records = [], maxProWrSeconds = COTW_MAX_PRO_WR_SECONDS } = {}
) => {
  if (!Array.isArray(maps)) return [];
  const authoritative = new Map();
  (Array.isArray(records) ? records : []).forEach((record) => {
    const time = recordTime(record);
    if (time == null || time < 0 || time >= maxProWrSeconds || !recordIsNoTeleportNoStyle(record))
      return;
    const key = `${recordMapKey(record)}|${recordMapName(record).toLowerCase()}|${recordCourseName(record).toLowerCase()}|${recordMode(record)}`;
    authoritative.set(key, record);
  });
  return maps
    .map((map) => {
      const courses = Array.isArray(map?.courses) ? map.courses : [];
      const eligibleCourses = courses.flatMap((course, index) =>
        COTW_MODES.map((mode) => {
          const eligibility = modeEligibility(course, mode.id, maxProWrSeconds);
          const authorityKey = `${mapKey(map)}|${mapName(map).toLowerCase()}|${String(course?.name || course?.courseName || `Course ${index + 1}`).toLowerCase()}|${mode.id}`;
          const authoritativeRecord = authoritative.get(authorityKey);
          // PRO WR is authoritative only when it came from /records. This
          // also permits real API rows that identify the map by name instead
          // of numeric id.
          const matchingRecord =
            authoritativeRecord ||
            [...authoritative.entries()].find(([key]) => {
              const [recordId, recordName, recordCourse, recordModeId] = key.split('|');
              return (
                recordModeId === mode.id &&
                recordCourse ===
                  String(
                    course?.name || course?.courseName || `Course ${index + 1}`
                  ).toLowerCase() &&
                (recordId === mapKey(map) || recordId === '') &&
                (recordName === mapName(map).toLowerCase() || recordName === '')
              );
            })?.[1];
          if (!matchingRecord) return null;
          // The record supplies authoritative time; map metadata is still used
          // for the ranked filter gate, but its absent WR must not exclude a
          // real top-record row.
          if (eligibility.reason === 'not-ranked') return null;
          return {
            map,
            mapId: mapKey(map),
            mapName: mapName(map),
            workshopId: map?.workshop_id ?? map?.workshopId ?? null,
            course,
            courseName: course?.name || course?.courseName || `Course ${index + 1}`,
            courseIndex: index + 1,
            mode: mode.id,
            modeShort: mode.short,
            modeLabel: mode.label,
            proWorldRecord: recordTime(matchingRecord),
            recordId: matchingRecord.id || null,
            state: eligibility.state,
          };
        }).filter(Boolean)
      );
      return eligibleCourses.length
        ? {
            map,
            mapId: mapKey(map),
            mapName: mapName(map),
            entries: eligibleCourses,
          }
        : null;
    })
    .filter(Boolean);
};

/** Flat view useful for admin previews and worker diagnostics. */
export const eligibleCotwEntries = (maps, options = {}) =>
  eligibleCotwMaps(maps, options).flatMap((group) => group.entries);

const defaultRandomBytes = (size) => {
  if (globalThis.crypto?.getRandomValues) {
    const bytes = new Uint8Array(size);
    globalThis.crypto.getRandomValues(bytes);
    return bytes;
  }
  throw new Error('A cryptographically secure random source is required for CotW selection');
};

/** Uniform random integer in [0, length), using rejection sampling. */
export const cryptoRandomIndex = (length, randomBytes = defaultRandomBytes) => {
  if (!Number.isInteger(length) || length <= 0) throw new RangeError('length must be positive');
  const byteWidth = length <= 256 ? 1 : 4;
  const range = byteWidth === 1 ? 256 : 2 ** 32;
  const limit = Math.floor(range / length) * length;
  for (;;) {
    const bytes = randomBytes(byteWidth);
    if (!bytes || bytes.length < byteWidth) {
      throw new TypeError('randomBytes must return byte-like data');
    }
    let value = 0;
    for (let i = 0; i < byteWidth; i += 1) value = value * 256 + bytes[i];
    if (!Number.isSafeInteger(value)) throw new TypeError('randomBytes must return byte-like data');
    if (value < limit) return value % length;
  }
};

/**
 * Pick a course uniformly by map and then uniformly by course/mode. The
 * random source is injectable for deterministic tests; production callers must
 * provide Node crypto.randomBytes or use a platform crypto.getRandomValues.
 */
export const pickEligibleCotw = (
  maps,
  {
    records = [],
    maxProWrSeconds = COTW_MAX_PRO_WR_SECONDS,
    randomBytes = defaultRandomBytes,
    randomIndex,
  } = {}
) => {
  const grouped = eligibleCotwMaps(maps, { records, maxProWrSeconds });
  if (!grouped.length) return null;
  const pick = (length) =>
    typeof randomIndex === 'function'
      ? (() => {
          const index = randomIndex(length);
          if (!Number.isInteger(index) || index < 0 || index >= length) {
            throw new RangeError('randomIndex must return an index in range');
          }
          return index;
        })()
      : cryptoRandomIndex(length, randomBytes);
  const mapGroup = grouped[pick(grouped.length)];
  const entry = mapGroup.entries[pick(mapGroup.entries.length)];
  return {
    ...entry,
    selection: {
      mapId: entry.mapId,
      mapName: entry.mapName,
      workshopId: entry.workshopId,
      courseName: entry.courseName,
      courseIndex: entry.courseIndex,
      mode: entry.mode,
      modeShort: entry.modeShort,
      proWorldRecord: entry.proWorldRecord,
    },
  };
};

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_COMPACT_RE = /^[0-9a-f]{32}$/i;

/**
 * Decode the millisecond timestamp from a UUIDv7, with strict version,
 * variant, and plausibility checks. CS2KZ does not expose submitted_at, so
 * this is the only place where its UUIDv7 id is interpreted.
 */
export const decodeUuidV7Timestamp = (
  id,
  { now = Date.now(), minMs = Date.UTC(2000, 0, 1), maxFutureMs = 5 * 60 * 1000, maxMs } = {}
) => {
  const raw = String(id || '').trim();
  if (!UUID_RE.test(raw) && !UUID_COMPACT_RE.test(raw)) return null;
  const hex = raw.replace(/-/g, '').toLowerCase();
  if (hex[12] !== '7') return null;
  // RFC 9562 variant 10xx (the first nibble of the fourth UUID group).
  if (!/[89ab]/.test(hex[16])) return null;
  const timestampMs = Number.parseInt(hex.slice(0, 12), 16);
  const upperBound = maxMs ?? now + maxFutureMs;
  if (!Number.isSafeInteger(timestampMs) || timestampMs < minMs || timestampMs > upperBound) {
    return null;
  }
  return timestampMs;
};

export const uuidV7Timestamp = decodeUuidV7Timestamp;

/** Decode a record's submission instant without accepting fabricated fields. */
export const submittedAtFromRecord = (record, options = {}) =>
  decodeUuidV7Timestamp(record?.id ?? record, options);

export const isPlausibleSubmissionTimestamp = (value, options = {}) =>
  decodeUuidV7Timestamp(value, options) != null ||
  (Number.isFinite(Number(value)) &&
    Number(value) >= (options.minMs ?? Date.UTC(2000, 0, 1)) &&
    Number(value) <=
      (options.maxMs ?? (options.now ?? Date.now()) + (options.maxFutureMs ?? 5 * 60 * 1000)));

const identityFields = [
  'eventId',
  'participantId',
  'uid',
  'recordId',
  'mapId',
  'courseName',
  'mode',
  'replayReference',
];

const replayPause = (replay) => {
  const finalAttempt = asObject(replay?.finalAttempt);
  return firstNumber(
    finalAttempt.pauseSeconds,
    finalAttempt.pause_seconds,
    replay?.pauseSeconds,
    replay?.pause_seconds
  );
};

/**
 * Validate the signed replay adapter's payload shape and identity binding.
 * Missing or malformed values are intentionally unverifiable, never accepted.
 */
export const normalizeReplayValidation = (replay, expected = {}) => {
  if (!replay || typeof replay !== 'object') {
    return { verifiable: false, reason: 'missing-replay-validation' };
  }
  if (replay.contractVersion !== COTW_REPLAY_CONTRACT_VERSION) {
    return { verifiable: false, reason: 'replay-contract-mismatch' };
  }
  if (replay.valid !== true) {
    return {
      verifiable: false,
      reason: replay.valid === false ? 'replay-invalid' : 'replay-not-verified',
    };
  }
  for (const field of identityFields) {
    if (expected[field] == null) continue;
    const actual =
      field === 'replayReference'
        ? normalizeReplayReference(
            replay.replayReference ??
              replay.replay_reference ??
              replay.replayId ??
              replay.identifiers?.replayReference ??
              replay.identifiers?.replay_reference ??
              replay.identifiers?.replayId
          )
        : (replay[field] ?? replay.identifiers?.[field]);
    const expectedValue =
      field === 'replayReference' ? normalizeReplayReference(expected[field]) : expected[field];
    if (actual == null || expectedValue == null || String(actual) !== String(expectedValue)) {
      return { verifiable: false, reason: `replay-${field}-mismatch` };
    }
  }
  const pauseSeconds = replayPause(replay);
  if (pauseSeconds == null || pauseSeconds < 0) {
    return { verifiable: false, reason: 'missing-pause-seconds' };
  }
  const replayReference = normalizeReplayReference(
    replay.replayReference ??
      replay.replay_reference ??
      replay.replayId ??
      replay.identifiers?.replayReference ??
      replay.identifiers?.replay_reference ??
      replay.identifiers?.replayId
  );
  if (!replayReference) {
    return { verifiable: false, reason: 'missing-replay-reference' };
  }
  return { verifiable: true, pauseSeconds, replayReference, replay };
};

/**
 * Assess one run for a round. `submittedAt` must come from the trusted worker
 * or from a plausibly decoded UUIDv7. The wall time includes final-attempt
 * pause time; pause DQ and unverifiable replay both fail closed.
 */
export const assessCotwSubmission = ({
  submission = {},
  replay,
  expected = {},
  roundStart,
  toleranceSeconds = COTW_PAUSE_TOLERANCE_SECONDS,
  now = Date.now(),
} = {}) => {
  const runtimeSeconds = firstNumber(
    submission.runtimeSeconds,
    submission.runtime,
    submission.time,
    submission.record?.time
  );
  if (runtimeSeconds == null || runtimeSeconds < 0) {
    return { accepted: false, status: 'rejected', reason: 'invalid-runtime' };
  }

  const rawSubmittedAt = firstDefined(
    submission.submittedAt,
    submission.recordSubmittedAt,
    submission.record?.submittedAt
  );
  // A malformed explicit value must not prevent the UUIDv7 fallback. The
  // CS2KZ API has no submitted_at field, so workers should leave this field
  // unset and let the isolated decoder below provide the trusted timestamp.
  const submittedAt =
    finiteNumber(rawSubmittedAt) ??
    submittedAtFromRecord(submission.record ?? submission.recordId, { now });
  if (submittedAt == null || !isPlausibleSubmissionTimestamp(submittedAt, { now })) {
    return { accepted: false, status: 'unverifiable', reason: 'unverifiable-submitted-at' };
  }
  const start = finiteNumber(roundStart);
  if (start == null || submittedAt < start) {
    return { accepted: false, status: 'unverifiable', reason: 'submission-before-round' };
  }

  const replayResult = normalizeReplayValidation(replay, {
    ...expected,
    recordId: expected.recordId ?? submission.recordId ?? submission.record?.id,
  });
  if (!replayResult.verifiable) {
    return { accepted: false, status: 'unverifiable', reason: replayResult.reason, submittedAt };
  }

  const availableSeconds = (submittedAt - start) / 1000;
  const wallSeconds = runtimeSeconds + replayResult.pauseSeconds;
  const tolerance = Math.max(0, finiteNumber(toleranceSeconds) ?? COTW_PAUSE_TOLERANCE_SECONDS);
  if (wallSeconds > availableSeconds + tolerance) {
    return {
      accepted: false,
      status: 'pause-dq',
      reason: 'pause-exceeds-available-time',
      runtimeSeconds,
      pauseSeconds: replayResult.pauseSeconds,
      wallSeconds,
      availableSeconds,
      submittedAt,
    };
  }
  return {
    accepted: true,
    status: 'accepted',
    runtimeSeconds,
    pauseSeconds: replayResult.pauseSeconds,
    wallSeconds,
    availableSeconds,
    submittedAt,
  };
};

const participantKey = (submission) =>
  String(
    submission?.participantId ?? submission?.uid ?? submission?.userId ?? submission?.id ?? ''
  );

const submissionKey = (submission) =>
  String(submission?.id ?? submission?.recordId ?? participantKey(submission));

const compareSubmissionOrder = (a, b) => {
  const aTime = finiteNumber(a?.submittedAt) ?? Number.POSITIVE_INFINITY;
  const bTime = finiteNumber(b?.submittedAt) ?? Number.POSITIVE_INFINITY;
  return aTime - bTime || submissionKey(a).localeCompare(submissionKey(b));
};

const compareRuntimeOrder = (a, b) => {
  const aRuntime = firstNumber(a?.runtimeSeconds, a?.runtime, a?.time) ?? Number.POSITIVE_INFINITY;
  const bRuntime = firstNumber(b?.runtimeSeconds, b?.runtime, b?.time) ?? Number.POSITIVE_INFINITY;
  return aRuntime - bRuntime || compareSubmissionOrder(a, b);
};

const compareNonfinishers = (a, b) =>
  participantKey(a).localeCompare(participantKey(b)) ||
  submissionKey(a).localeCompare(submissionKey(b));

const isAccepted = (submission) =>
  submission?.accepted === true || submission?.status === 'accepted' || submission?.valid === true;

/** Number of players removed from one knockout round. */
export const knockoutEliminationCount = (activeCount, finalists = COTW_FINALISTS) => {
  const active = Math.max(0, Math.floor(Number(activeCount) || 0));
  const finalCount = Math.max(1, Math.floor(Number(finalists) || COTW_FINALISTS));
  if (active <= finalCount) return 0;
  return Math.min(active - finalCount, Math.max(1, Math.floor(active * 0.1)));
};

export const knockoutAdvanceCount = (activeCount, finalists = COTW_FINALISTS) =>
  Math.max(
    0,
    Math.floor(Number(activeCount) || 0) - knockoutEliminationCount(activeCount, finalists)
  );

/**
 * Rank one knockout round with the ten-second boundary buffer.
 *
 * Finishes before the buffer retain first-valid-finish order. The boundary
 * block is reordered by runtime, then submission time/id. Anything submitted
 * after the block remains below it. Nonfinishers are always last in stable
 * participant/id order, so a replay/API outage cannot create nondeterministic
 * advancement.
 */
export const rankKnockoutRound = (
  submissions,
  {
    activeCount = Array.isArray(submissions) ? submissions.length : 0,
    finalists = COTW_FINALISTS,
    bufferSeconds = COTW_BUFFER_SECONDS,
  } = {}
) => {
  const rows = Array.isArray(submissions) ? submissions : [];
  const accepted = rows.filter(isAccepted).sort(compareSubmissionOrder);
  const nonfinishers = rows.filter((row) => !isAccepted(row)).sort(compareNonfinishers);
  const eliminationCount = knockoutEliminationCount(activeCount, finalists);
  // There is no boundary block when fewer than N + 1 valid finishes exist.
  // Clamping the index to zero would incorrectly move the first finisher into
  // a buffer intended for the elimination boundary.
  const anchorIndex = accepted.length - (eliminationCount + 1);
  const anchorEntry = anchorIndex >= 0 ? accepted[anchorIndex] : null;
  const anchorSubmittedAt = finiteNumber(anchorEntry?.submittedAt);
  const bufferEnd =
    anchorSubmittedAt == null
      ? null
      : anchorSubmittedAt + Math.max(0, Number(bufferSeconds) || 0) * 1000;

  let before = accepted;
  let buffer = [];
  let after = [];
  if (anchorSubmittedAt != null) {
    before = accepted.filter((row) => row.submittedAt < anchorSubmittedAt);
    buffer = accepted
      .filter((row) => row.submittedAt >= anchorSubmittedAt && row.submittedAt <= bufferEnd)
      .sort(compareRuntimeOrder);
    after = accepted.filter((row) => row.submittedAt > bufferEnd).sort(compareSubmissionOrder);
  }

  const ordered = [...before, ...buffer, ...after, ...nonfinishers].map((row, index) => ({
    ...row,
    rank: index + 1,
    finish: isAccepted(row),
    boundary: buffer.includes(row) ? 'buffer' : after.includes(row) ? 'after-buffer' : null,
  }));
  const advanceCount = knockoutAdvanceCount(activeCount, finalists);
  const survivors = ordered.slice(0, advanceCount);
  const eliminated = ordered.slice(advanceCount);
  return {
    ordered,
    survivors,
    eliminated,
    accepted,
    nonfinishers,
    eliminationCount,
    advanceCount,
    anchorSubmittedAt,
    bufferEnd,
    buffer,
    afterBuffer: after,
  };
};

export const orderKnockoutSubmissions = rankKnockoutRound;
export const rankRound = rankKnockoutRound;

/** Qualifier rule: every valid 20-minute finisher advances. */
export const qualifierAdvancers = (submissions) =>
  (Array.isArray(submissions) ? submissions : []).filter(isAccepted).sort(compareRuntimeOrder);

export const rankQualifier = qualifierAdvancers;

/** Build an event before its map is revealed; no hidden preselection is stored. */
export const createCotwEvent = ({
  id,
  eventDate,
  startsAt,
  timeZone = COTW_TIME_ZONE,
  qualifierMinutes = COTW_QUALIFIER_MINUTES,
  knockoutMinutes = COTW_KNOCKOUT_MINUTES,
  interRoundSeconds = COTW_INTERROUND_SECONDS,
  finalists = COTW_FINALISTS,
} = {}) => {
  const start = finiteNumber(startsAt) ?? cotwStartForDate(eventDate, timeZone);
  return {
    id: id || eventDate,
    eventDate,
    timeZone,
    qualifierStartsAt: start,
    qualifierEndsAt: start + qualifierMinutes * 60 * 1000,
    qualifierMinutes,
    knockoutMinutes,
    interRoundSeconds,
    finalists,
    phase: COTW_PHASES.UPCOMING,
    participantCount: 0,
    activeCount: 0,
    roundSequence: 0,
    // `selection` is intentionally absent until revealCotwSelection runs.
    createdAt: Date.now(),
  };
};

/** Reveal the server-selected map/course/mode at qualifier start. */
export const revealCotwSelection = (event, selection, at = Date.now()) => {
  if (!selection?.mapId || !selection?.courseName || !selection?.mode) {
    throw new Error('A complete CotW selection is required');
  }
  const revealedAt = finiteNumber(at) ?? Date.now();
  return {
    ...event,
    selection: {
      mapId: selection.mapId,
      mapName: selection.mapName || String(selection.mapId),
      workshopId: selection.workshopId ?? null,
      courseName: selection.courseName,
      courseIndex: selection.courseIndex ?? null,
      mode: normaliseMode(selection.mode)?.id || selection.mode,
      modeShort: normaliseMode(selection.mode)?.short || null,
      proWorldRecord: selection.proWorldRecord ?? null,
    },
    revealedAt,
    phase: COTW_PHASES.QUALIFIER,
    qualifierStartsAt: revealedAt,
    qualifierEndsAt:
      revealedAt + (Number(event.qualifierMinutes) || COTW_QUALIFIER_MINUTES) * 60 * 1000,
  };
};

export const startKnockoutRound = (event, { roundId, roundNumber, startsAt, activeCount } = {}) => {
  const start = finiteNumber(startsAt) ?? Date.now();
  const endsAt = start + (Number(event?.knockoutMinutes) || COTW_KNOCKOUT_MINUTES) * 60 * 1000;
  return {
    ...event,
    phase: COTW_PHASES.KNOCKOUT,
    currentRound: {
      id: roundId || `round-${roundNumber ?? 1}`,
      number: roundNumber ?? 1,
      startsAt: start,
      endsAt,
      status: 'live',
      activeCount: activeCount ?? event.activeCount ?? 0,
    },
  };
};

export const finishCotwEvent = (event, at = Date.now()) => ({
  ...event,
  phase: COTW_PHASES.COMPLETE,
  completedAt: finiteNumber(at) ?? Date.now(),
  currentRound: null,
});

export const nextRoundStartsAt = (roundEndsAt, interRoundSeconds = COTW_INTERROUND_SECONDS) =>
  finiteNumber(roundEndsAt) == null
    ? null
    : finiteNumber(roundEndsAt) + Math.max(0, Number(interRoundSeconds) || 0) * 1000;
